import React, {
  forwardRef,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Box, Stack, Typography } from '@mui/material';
import {
  addWeeks,
  differenceInCalendarWeeks,
  eachDayOfInterval,
  endOfWeek,
  format,
  getISOWeek,
  isSameDay,
  isSameWeek,
  startOfDay,
} from 'date-fns';
import { PurchaseOrderItem } from '@domain/purchasing';
import { APP_CONFIG } from '../../../../config/app.config';
import {
  WEEKS_AFTER,
  WEEKS_BEFORE,
  WEEK_WIDTH,
  SLIDE_MS,
  dayKey,
  dayToneFromItems,
} from './utils';

export interface PurchaseOrderWeekStripHandle {
  selectWeek: (weekStart: Date) => void;
}

interface PurchaseOrderWeekStripProps {
  selectedWeekStart: Date;
  currentWeekStart: Date;
  today: Date;
  itemsByDay: Map<string, PurchaseOrderItem[]>;
  onSelectWeek: (weekStart: Date) => void;
}

const PurchaseOrderWeekStrip = forwardRef<
  PurchaseOrderWeekStripHandle,
  PurchaseOrderWeekStripProps
>(function PurchaseOrderWeekStrip(
  {
    selectedWeekStart,
    currentWeekStart,
    today,
    itemsByDay,
    onSelectWeek,
  },
  ref
) {
  const colors = APP_CONFIG.ui.theme.colors;
  const [slideOffsetPx, setSlideOffsetPx] = useState(0);
  const [slideDelta, setSlideDelta] = useState(0);
  const [pendingWeekStart, setPendingWeekStart] = useState<Date | null>(null);
  const [disableSlideTransition, setDisableSlideTransition] = useState(false);
  const isAnimatingRef = useRef(false);

  const highlightedWeekStart = pendingWeekStart ?? selectedWeekStart;

  const weeks = useMemo(() => {
    const extraBefore = Math.max(0, -slideDelta);
    const extraAfter = Math.max(0, slideDelta);
    const startOffset = WEEKS_BEFORE + extraBefore;
    const endOffset = WEEKS_AFTER + extraAfter;
    const count = startOffset + 1 + endOffset;

    return Array.from({ length: count }, (_, index) => {
      const weekStart = addWeeks(selectedWeekStart, index - startOffset);
      const days = eachDayOfInterval({
        start: weekStart,
        end: endOfWeek(weekStart, { weekStartsOn: 1 }),
      });
      return {
        weekStart,
        weekNumber: getISOWeek(weekStart),
        isSelected: isSameWeek(weekStart, highlightedWeekStart, {
          weekStartsOn: 1,
        }),
        isActualCurrentWeek: isSameWeek(weekStart, currentWeekStart, {
          weekStartsOn: 1,
        }),
        days,
      };
    });
  }, [selectedWeekStart, currentWeekStart, slideDelta, highlightedWeekStart]);

  const handleSelectWeek = (weekStart: Date) => {
    if (isAnimatingRef.current) return;
    if (isSameWeek(weekStart, selectedWeekStart, { weekStartsOn: 1 })) {
      onSelectWeek(weekStart);
      return;
    }

    const delta = differenceInCalendarWeeks(weekStart, selectedWeekStart, {
      weekStartsOn: 1,
    });
    isAnimatingRef.current = true;
    setPendingWeekStart(weekStart);
    setSlideDelta(delta);

    if (delta < 0) {
      setDisableSlideTransition(true);
      setSlideOffsetPx(delta * WEEK_WIDTH);
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          setDisableSlideTransition(false);
          setSlideOffsetPx(0);
        });
      });
      return;
    }

    setDisableSlideTransition(false);
    setSlideOffsetPx(0);
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        setSlideOffsetPx(-delta * WEEK_WIDTH);
      });
    });
  };

  useImperativeHandle(ref, () => ({
    selectWeek: handleSelectWeek,
  }));

  const handleSlideTransitionEnd = (
    event: React.TransitionEvent<HTMLDivElement>
  ) => {
    if (event.propertyName !== 'transform') return;
    if (!pendingWeekStart) return;

    setDisableSlideTransition(true);
    onSelectWeek(pendingWeekStart);
    setPendingWeekStart(null);
    setSlideDelta(0);
    setSlideOffsetPx(0);

    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        setDisableSlideTransition(false);
        isAnimatingRef.current = false;
      });
    });
  };

  const renderDayCell = (date: Date) => {
    const key = dayKey(date);
    const dayItems = itemsByDay.get(key) ?? [];
    const isToday = isSameDay(date, today);
    const tone = dayToneFromItems(dayItems, today, startOfDay(date));

    const bg =
      tone === 'idle'
        ? colors.border
        : tone === 'success'
          ? colors.success
          : tone === 'error'
            ? colors.error
            : colors.primary;

    const textColor =
      tone === 'idle'
        ? isToday
          ? colors.primaryText
          : colors.secondaryText
        : colors.buttonContent;

    return (
      <Box
        key={key}
        aria-label={format(date, 'EEEE, MMM d')}
        sx={{
          position: 'relative',
          flex: '1 0 0',
          minWidth: 0,
          aspectRatio: '1 / 1',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          borderRadius: '4px',
          bgcolor: bg,
          p: 0,
          overflow: 'visible',
        }}
      >
        <Typography
          variant='caption'
          fontSize={10}
          color={textColor}
        >
          {date.getDate()}
        </Typography>
      </Box>
    );
  };

  return (
    <Box
      sx={{
        px: 2,
        pb: 2,
        opacity: 1,
        overflow: 'hidden',
        width: '100%',
        borderBottom: `1px solid ${colors.border}`,
        display: 'flex',
        justifyContent: 'center',
      }}
    >
      <Box
        sx={{
          width: WEEK_WIDTH * (WEEKS_BEFORE + 1 + WEEKS_AFTER),
          overflow: 'hidden',
          flexShrink: 0,
        }}
      >
        <Box
          onTransitionEnd={handleSlideTransitionEnd}
          sx={{
            display: 'flex',
            alignItems: 'flex-end',
            transform: `translateX(${slideOffsetPx}px)`,
            transition: disableSlideTransition
              ? 'none'
              : `transform ${SLIDE_MS}ms ease`,
            willChange: 'transform',
          }}
        >
          {weeks.map((week) => (
            <Stack
              key={dayKey(week.weekStart)}
              component="button"
              type="button"
              onClick={() => handleSelectWeek(week.weekStart)}
              alignItems="center"
              aria-pressed={week.isSelected}
              aria-label={
                week.isActualCurrentWeek
                  ? `This week, ${format(week.days[3], 'MMMM')}`
                  : `Week ${week.weekNumber}, ${format(week.days[3], 'MMMM')}`
              }
              sx={{
                width: WEEK_WIDTH,
                minWidth: WEEK_WIDTH,
                flexShrink: 0,
                cursor: 'pointer',
                background: 'none',
                p: 1,
                borderRadius: week.isSelected ? '8px' : '4px',
                gap: 1,
                border: week.isSelected
                  ? `1px solid ${colors.primary}`
                  : 'none',
                ...(week.isSelected
                  ? {
                      bgcolor: 'rgba(0, 132, 255, 0.1)',
                    }
                  : {
                      '&:hover': {
                        bgcolor: colors.backgroundTint,
                      },
                    }),
              }}
            >
              <Stack
                direction="row"
                gap={0.5}
                width="100%"
                alignItems="flex-end"
              >
                {week.days.map(renderDayCell)}
              </Stack>
              <Box
                sx={{
                  width: '100%',
                  bgcolor: week.isSelected ? colors.primary : colors.border,
                  borderRadius: '4px',
                  px: '2px',
                  display: 'flex',
                  justifyContent: 'center',
                  height: 20,
                }}
              >
                <Typography
                variant='caption'
                  sx={{
                    color: week.isSelected
                      ? colors.buttonContent
                      : colors.secondaryText,
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  }}
                >
                  {week.isActualCurrentWeek
                    ? `This Week · ${format(week.days[3], 'MMM')}`
                    : `Week ${week.weekNumber} · ${format(week.days[3], 'MMM')}`}
                </Typography>
              </Box>
            </Stack>
          ))}
        </Box>
      </Box>
    </Box>
  );
});

export default PurchaseOrderWeekStrip;
